import React, {useEffect, useState} from 'react'
import {useLocation, useNavigate} from "react-router-dom";
import {get, post} from "../../utils/fetchAPI";
import PageTitle from "../../components/page-title/pageTitle";
import {BiSolidUser} from "react-icons/bi";
import {Bounce, toast} from "react-toastify";

const CreateStaff = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const editingUser = location.state?.user || null;
    // Role defaults to "staff"; the role picker passes "assistant" to reuse this form.
    const roleType = editingUser?.type || location.state?.role || 'staff';
    const roleLabel = roleType.charAt(0).toUpperCase() + roleType.slice(1);
    const [changeData, setChangeData] = useState([])

    const [staffData, setStaffData] = useState({
        name: '',
        email: '',
        phone:'',
        password:'',
        dob: '',
        gender:'',
        address: '',
        type: roleType,
        status: ''
    });

    useEffect(() => {
        if (editingUser) {
            setStaffData({
                ...editingUser,
                dob: editingUser.dob
                    ? new Date(editingUser.dob).toISOString().split("T")[0]
                    : "",
                type: roleType,
            });
        }
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setStaffData((prev) => ({
            ...prev,
            [name]: value
        }))

        setChangeData((prev) => ({
            ...prev,
            [name]: value
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        try {
            if (editingUser) {
                // UPDATE USER
                const updateData = { ...changeData };
                delete changeData.password;
                await post(`user/update/${editingUser._id}`, updateData);
                toast.success(`${roleLabel} updated successfully!`, {
                    position: "top-center",
                    transition: Bounce,
                });
            } else {
                // CREATE USER
                await post("user/create", staffData);
                toast.success(`${roleLabel} created successfully!`, {
                    position: "top-center",
                    transition: Bounce,
                });
            }

            // await fetchUser();
            navigate("/doctor-list");
        }catch (error) {
            if (error.response?.status === 409) {
                toast.error("This email is already used!", {
                    position: "top-center",
                });
            } else {
                toast.error("Failed to save doctor info.", {
                    position: "top-center",
                });
            }
        }
    }

    return (
        <div className="w-full p-5">
            <div className="flex justify-between items-center mb-5">
                <PageTitle
                    icon={<BiSolidUser />}
                    title={editingUser ? `Edit ${roleLabel}` : `Create ${roleLabel}`}
                />

                <button
                    onClick={() => navigate("/doctor-list")}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md text-sm"
                >
                    Back
                </button>
            </div>

            {/* FORM */}
            <div className="bg-white shadow-md rounded-md p-6">
                <h2 className="text-blue-500 text-2xl font-bold mb-4">
                    {editingUser ? `Edit ${roleLabel} Info` : `Create New ${roleLabel}`}
                </h2>

                <form
                    className="text-black text-sm flex flex-col w-full max-h-[35rem] overflow-y-auto"
                    onSubmit={handleSubmit}
                >
                    {/* Name + Email */}
                    <div className="flex flex-col lg:flex-row gap-5 mb-3">
                        <div className="w-full">
                            <label>Name *</label>
                            <input
                                name="name"
                                value={staffData?.name}
                                placeholder={`${roleLabel} Name`}
                                onChange={handleInputChange}
                                className="border p-2 rounded-md w-full"
                                required
                            />
                        </div>

                        <div className="w-full">
                            <label>Email *</label>
                            <input
                                name="email"
                                value={staffData?.email}
                                placeholder="Email"
                                onChange={handleInputChange}
                                className="border p-2 rounded-md w-full"
                                required
                            />
                        </div>
                    </div>

                    {/* Password */}
                    {!editingUser && (
                        <div className="flex flex-col lg:flex-row gap-5 mb-3">
                            <div className="w-full">
                                <label>Password *</label>
                                <input
                                    type="password"
                                    name="password"
                                    value={staffData?.password}
                                    placeholder="Password"
                                    onChange={handleInputChange}
                                    className="border p-2 rounded-md w-full"
                                    required
                                />
                            </div>
                        </div>
                    )}

                    {/* Address + Status */}
                    <div className="flex flex-col lg:flex-row gap-5 mb-3">
                        <div className="w-full">
                            <label>Address <span className="text-red-500">*</span></label>
                            <input
                                name="address"
                                value={staffData?.address}
                                placeholder="Address"
                                onChange={handleInputChange}
                                className="border p-2 rounded-md w-full"
                                required
                            />
                        </div>

                        <div className="w-full">
                            <label>Status *</label>
                            <select
                                name="status"
                                value={staffData?.status}
                                onChange={handleInputChange}
                                className="border p-2 rounded-md w-full"
                                required
                            >
                                <option value="">Select Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>

                    {/* Doctor Type Auto-Fixed */}
                    <div className="w-full mb-3">
                        <label>Type *</label>
                        <input
                            value={roleType}
                            readOnly
                            className="border p-2 rounded-md bg-gray-100 cursor-not-allowed w-full"
                        />
                    </div>

                    {/* DOB + Gender */}
                    <div className="flex flex-col lg:flex-row gap-5 mb-3">
                        <div className="w-full">
                            <label>Date of Birth *</label>
                            <input
                                type="date"
                                name="dob"
                                value={staffData?.dob}
                                onChange={handleInputChange}
                                className="border p-2 rounded-md w-full"
                                required
                            />
                        </div>

                        <div className="w-full">
                            <label>Gender *</label>
                            <select
                                name="gender"
                                value={staffData?.gender}
                                onChange={handleInputChange}
                                className="border p-2 rounded-md w-full"
                                required
                            >
                                <option value="">Select Gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                            </select>
                        </div>
                    </div>

                    {/* Phone */}
                    <div className="mb-3">
                        <label>Phone</label>
                        <input
                            name="phone"
                            type="number"
                            value={staffData?.phone}
                            placeholder="Phone Number"
                            onChange={handleInputChange}
                            className="border p-2 rounded-md w-full"
                        />
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-4 mt-6">
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-500 text-white rounded-md"
                        >
                            {editingUser ? "Update" : "Create"}
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate("/doctor-list")}
                            className="px-4 py-2 bg-gray-300 rounded-md"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
export default CreateStaff
